import { createSelector } from "reselect";
import { endOfToday } from "./time";
import getDefaultTab from "./get-default-tab";
import { getTournamentsForList as gtfl, getFilteredTournamentsForList as gftfl } from "./get-tournaments-for-list";
import { List } from "immutable";
import { parse } from "query-string";
import payouts from "./payouts";
import survivorsForTournament from "./survivors-for-tournament";
import ticketsForTournament from "./tickets-for-tournament";

export const getAlert = (state) => state.alert;
export const getBets = (state) => state.bets;
export const getCommunications = (state) => state.communications;
export const getCurrentUserId = (state) => state.session.get("user_id");
export const getDeposit = (state) => state.deposit;
export const getExpandedRace = (state) => state.ui.getIn(["gameBoard", "expandedRace"]);
export const getGroupBettingPools = (state) => state.groupBettingPools;
export const getMonthlyWinsLeaders = (state) => state.monthlyLeaders.get("wins");
export const getMonthlyWinningsLeaders = (state) => state.monthlyLeaders.get("winnings");
export const getMyGames = (state) => state.myGames;
export const getRaces = (state) => state.races;
export const getRaceEntrants = (state) => state.raceEntrants;
export const getServerTimeDifference = (state) => state.time.get("serverTimeDifference");
export const getSession = (state) => state.session;
export const getTags = (state) => state.tags;
export const getTickets = (state) => state.tickets;
export const getTicketScores = (state) => state.ticketScores;
export const getTokenTypes = (state) => state.tokenTypes;
export const getTournament = (state, { match, tournament }) => tournament || state.tournaments.get(parseInt(match.params.id));
export const getWithdraw = (state) => state.withdraw;

export const getBetsForTicket = (state, { ticketId }) => {
  if (typeof ticketId === "undefined") {
    return List();
  }

  return getBets(state)
    .valueSeq()
    .flatten(1)
    .filter(bet => bet.get("ticket_id") === parseInt(ticketId))
    .toList();
};

export const getRaceEntrantsForBetsForTicket = createSelector(getRaceEntrants, getBetsForTicket,
  (raceEntrants, bets) => {
    var entrantIds = bets.reduce((acc, bet) => {
      var tmp1 = acc.push(bet.get("race_entrant_id"));
      var tmp2 = tmp1.push(bet.get("alternate_pick_id"));
      return tmp2;
    }, new List());

    var entrants = raceEntrants
      .valueSeq()
      .filter(entrant => entrantIds.includes(entrant.get("id")))
      .toList();

    return entrants;
  }
);

export const getPayouts = createSelector(getTournament, getTokenTypes, (tournament, tokenTypes) => payouts(tournament, tokenTypes));

export const getTournamentListFromUrl = (state, { match }) => match.params.list;
export const getTournamentList = createSelector(getTournamentListFromUrl, getSession,
  (list, session) => list || getDefaultTab(session)
);

export const getTournamentFilter = (state, { location }) => parse(location.search);
export const getTournaments = (state) => state.tournaments;
export const getTournamentsForList = createSelector(getTournaments, getTournamentList, getTags, gtfl);
export const getFilteredTournamentsForList = createSelector(getTournamentsForList, getTournamentList, getTournamentFilter, getTokenTypes, getRaces, getTags, gftfl);

export const getTransactions = (state) => state.transactions;
export const getTransactionsSorted = createSelector(getTransactions,
  (transactions) =>
    transactions
      .valueSeq()
      .sort((a, b) => new Date(b.get("created_at")) - new Date(a.get("created_at")) || b.get("id") - a.get("id"))
);

export const getUsers = (state) => state.users;
export const getTournamentChats = (state) => state.tournamentChats;

export const getRacesForTournament = createSelector(getRaces, getTournament,
  (races, tournament) => {
    if (tournament === undefined) {
      return List();
    }
    return tournament
      .get("races")
      .map((raceId) => races.get(raceId))
      .filter(race => race !== undefined);
  }
);

export const getRacesForTicket = (state, { ticketId }) => {
  if (typeof ticketId === "undefined" || parseInt(ticketId) === -1) {
    return List();
  }

  var currentTicket = getMyTickets(state)
    .valueSeq()
    .filter(ticket => ticket.get("id") === parseInt(ticketId))
    .first();

  return getTournaments(state)
    .valueSeq()
    .filter(tournament => tournament.get("id") === currentTicket.get("tournament_id"))
    .first()
    .get("races")
    .map((raceId) => getRaces(state).get(raceId))
    .filter(race => race !== undefined);
};

export const getTicketsForTournament = createSelector(getTickets, getTournament, ticketsForTournament);

export const getMyTickets = createSelector(getCurrentUserId, getTickets,
  (currentUser, tickets) => tickets.filter((ticket) => ticket.get("user_id") === currentUser)
);

export const getTicketsForRacesInCurrentTournament = createSelector(getTournament, getMyTickets, getBets,
  (tournament, myTickets, bets) => {
    var myTicketIds = myTickets.map((ticket) => ticket.get("id"));
    var myTicketIdsWithImportableBets =
      bets
        .valueSeq()
        .flatten(1)
        .filter(bet => myTicketIds.includes(bet.get("ticket_id")) && tournament.get("races").includes(bet.get("race_id")))
        .map(bet => bet.get("ticket_id"));

    return myTickets
      .filter(ticket => myTicketIdsWithImportableBets.valueSeq().includes(ticket.get("id")))
      .toSet();
  }
);

const BumpRunningRacesByMinutes = 5;

function getNextRace(tournament, races) {
  return tournament.get("races")
    .map((raceId) => races.get(raceId))
    .filter((race) => race.get("status") === "open" || race.get("status") === "running")
    .sort((a, b) => {
      var aPost = new Date(a.get("post_time"));
      if (a.get("status") === "running") {
        aPost.setMinutes(aPost.getMinutes() + BumpRunningRacesByMinutes);
      }
      var bPost = new Date(b.get("post_time"));
      if (b.get("status") === "running") {
        bPost.setMinutes(bPost.getMinutes() + BumpRunningRacesByMinutes);
      }
      return aPost - bPost;
    }).first();
}

function compareTournamentsForMyTicketsToday(a, b) {
  return new Date(a.get("starts_at")).getTime() - new Date(b.get("starts_at")).getTime();
}

export const getTournamentsForMyTicketsToday = createSelector(getMyTickets, getRaces, getTickets, getTournaments,
  (myTickets, races, tickets, tournaments) => myTickets
    .valueSeq()
    .map((ticket) => ticket.get("tournament_id"))
    .toSet()
    .map((tournamentId) => tournaments.get(tournamentId))
    .filter((tournament) => {
      if (tournament === undefined) {
        return false;
      }
      var starts = new Date(tournament.get("starts_at"));

      // calculate 4am local time on the following day
      var cutoff = endOfToday();

      return ["open", "running", "scheduled"].indexOf(tournament.get("status")) !== -1 &&
        starts < cutoff;
    })
    .map((tournament) => enhanceTournament(tournament, tickets, races))
    .sort(compareTournamentsForMyTicketsToday)
);

export const getTournamentsForMyGamesCompleted = createSelector(getMyGames, getRaces, getTickets, getTournaments,
  (myGames, races, tickets, tournaments) => getTournamentsForMyGames(myGames, races, tickets, tournaments, invertComparator(compareTournamentsByStartsAt))
);

export const getTournamentsForMyGamesFuture = createSelector(getMyGames, getRaces, getTickets, getTournaments,
  (myGames, races, tickets, tournaments) => getTournamentsForMyGames(myGames, races, tickets, tournaments, compareTournamentsByStartsAt)
);

function getTournamentsForMyGames(myGames, races, tickets, tournaments, comparator) {
  return (myGames.get("ids") || List())
    .map((tournamentId) => tournaments.get(tournamentId))
    .filter((tournament) => tournament !== undefined)
    .map((tournament) => enhanceTournament(tournament, tickets, races))
    .sort(comparator);
}

function enhanceTournament(tournament, tickets, races) {
  var rankedTickets = ticketsForTournament(tickets, tournament);
  return tournament
    .set("rankedTickets", rankedTickets)
    .set("nextRace", getNextRace(tournament, races));
}

function invertComparator(comparator) {
  return (a, b) => comparator(a, b) * -1;
}

function compareTournamentsByStartsAt(a, b) {
  var at = a.get("starts_at");
  if (!at) {
    return 1;
  }

  var bt = b.get("starts_at");
  if (!bt) {
    return -1;
  }

  return new Date(at).getTime() - new Date(bt).getTime();
}

export const getMyTicketsForTournament = createSelector(getCurrentUserId, getTicketsForTournament,
  (currentUser, tickets) => tickets.filter((ticket) => ticket.get("user_id") === currentUser).sort(compareMyTickets)
);

const compareMyTickets = (ticketA, ticketB) => {
  var eliminatedA = ticketA.get("eliminated_by_race_id") === null ? 0 : 1;
  var eliminatedB = ticketB.get("eliminated_by_race_id") === null ? 0 : 1;
  var eliminatedCmp = eliminatedA - eliminatedB;
  if (eliminatedCmp !== 0) {
    return eliminatedCmp;
  }
  return ticketA.get("ticket_number") - ticketB.get("ticket_number");
};

export const getSurvivorsForTournament = createSelector(getRacesForTournament, getTicketsForTournament, survivorsForTournament);

export const getChatsForTournament = createSelector(getTournamentChats, getTournament,
  (tournamentChats, tournament) => {
    if (tournament === undefined) {
      return List();
    }
    return tournamentChats.get(tournament.get("id"), List());
  }
);
